import { useState, useCallback, useEffect } from "react";
import SectionContainer from "../../components/UI/SectionContainer";
import UserSearchBox from "../../components/UI/UserSearchBox";
import Pagination from "../../components/UI/Pagination";
import DateTimeCell from '../../components/UI/DateTimeCell';
import { openAlert, openConfirm, USER_STATE, exportExcel, showPopup } from '../../utils/common';
import Spinner from '../../components/UI/Spinner';
import userService from '../../services/userService';

const AccountListPage = () => {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKind, setSearchKind] = useState('0');
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const pageSize = 10;
    const pageBlock = 5;

    const fetchList = useCallback(async (page = 1, skArg, kwArg) => {
      setLoading(true);
      try {
        const sk = skArg !== undefined ? Number(skArg) : 0;
        const kwState = kwArg !== undefined ? kwArg : '';
        
        const response = await userService.getUserList(
          page,
          pageSize,
          sk,
          kwState,
          USER_STATE.NORMAL
        );
        
        if (response.success) {
          setList(response.data.users);
          setTotalCount(response.data.pagination.totalCount);
        } else {
          openAlert({ title: '알림', description: response.message });
        }
      } catch (error) { 
        console.error('fetchList error:', error); 
        openAlert({ title: '알림', description: error.message || '데이터 로드 중 오류가 발생했습니다.' });
      } finally {
        setLoading(false);
      }
    }, []); 

    useEffect(() => {
      fetchList(currentPage, searchKind, keyword);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (directKeyword) => { 
      setCurrentPage(1); 
      // Use directKeyword if provided (from suggestion select), otherwise use keyword from state
      fetchList(1, searchKind, directKeyword !== undefined ? directKeyword : keyword); 
    };
    const handleReset = () => { 
      setSearchKind('0'); 
      setKeyword(''); 
      setCurrentPage(1); 
      // Call fetchList directly with reset values, consistent with new handleSearch signature
      fetchList(1, '0', ''); 
    };

    const requestUserSignout = useCallback(async ({userIdx}) => {
        setLoading(true);
        try {
            const response = await userService.signoutUser(userIdx);
            
            if (response.success) {
                openAlert({ 
                    title: '알림', 
                    description: '선택하신 회원이 탈퇴처리되었습니다.',
                    response_method: "fetchList"
                });
                fetchList();
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) { 
            console.error('requestUserSignout error:', error); 
            openAlert({ title: '알림', description: error.message || '사용자 탈퇴 처리 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    }, [fetchList]);

    // Expose for confirm callback
    useEffect(() => {
        window.requestUserSignout = requestUserSignout;
        window.fetchList = fetchList; 
        return () => { delete window.requestUserSignout; delete window.fetchList; };
    }, [requestUserSignout, fetchList]);

    const handleSignout = (userIdx) => {
        openConfirm({
            title: "탈퇴지정",
            description: "선택하신 회원을 정말로 탈퇴시키겠습니까?",
            positive_method : "requestUserSignout", //예를 눌렀을때 실행되는 함수
            positive_param : {userIdx: userIdx}, //positive_method 실행시 전달되는 parameter
            negative_method : null, //아니오를 눌렀을때 실행되는 함수
            negative_param : null, //negative_method 실행시 전달되는 parameter
        });
    }

    const handleExcelDownload = async () => {
        setLoading(true);
        try {
            const response = await userService.getUserExcelData(
                Number(searchKind),
                keyword,
                USER_STATE.NORMAL
            );
            
            if (response.success) {
                const excelConfig = {
                    excelFileName : "회원목록.xlsx",
                    sheetName : "Sheet1"
                }
                exportExcel(response.data, excelConfig);
            } else {
                openAlert({ title: '알림', description: response.message });
            }
        } catch (error) {
            console.error('handleExcelDownload error:', error);
            openAlert({ title: '알림', description: error.message || '엑셀 다운로드 중 오류가 발생했습니다.' });
        } finally {
            setLoading(false);
        }
    }

    const showUserDetail = (idx) => {
        showPopup(`/user/profile/${idx}`, 'userinfo');
    }

    return (
        <SectionContainer title="회원정보관리">
            <UserSearchBox
              value={keyword}
              userState={USER_STATE.NORMAL}
              onChange={setKeyword}
              searchKind={searchKind}
              onSelect={setSearchKind}
              onSearch={handleSearch}
              onReset={handleReset}
              onSuggestSelect={selectedVal => { 
                setKeyword(selectedVal); // Update the keyword state
                handleSearch(selectedVal); // And immediately search with this selected value
              }}
              totalCount={totalCount}
              onExcelDownload={handleExcelDownload}
            />
            <div className="content_area">
              <Spinner visible={loading} />
              <div className="table_area">
                <div className="table">
                  <table className="account-list-table">
                    <thead>
                      <tr>
                        <th>성명</th>
                        <th>아이디</th>
                        <th>이메일</th>
                        <th>가입일</th>
                        <th>최근 접속일</th>
                        <th>비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!loading && list.length === 0 ? (
                        <tr><td colSpan="6">회원 내역이 존재하지 않습니다.</td></tr>
                      ) : (
                        list.map((item, idx) => (
                          <tr key={idx}>
                            <td className="clickable" onClick={() => showUserDetail(item.userIdx)} style={{cursor: 'pointer'}}>{item.userName}</td>
                            <td style={{cursor: 'default'}}>{item.userId}</td>  
                            <td style={{cursor: 'default'}}>{item.userEmail}</td>
                            <DateTimeCell value={item.userRegDate} style={{cursor: 'default'}} />
                            <DateTimeCell value={item.accessDate} style={{cursor: 'default'}} />
                            <td style={{cursor: 'default'}}>
                              <div className="but-flex">
                                <div className="t_but mr-1" onClick={() => handleSignout(item.userIdx)}>탈퇴지정</div>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination currentPage={currentPage} totalCount={totalCount} pageSize={pageSize} pageBlock={pageBlock} onPageChange={setCurrentPage} />
        </SectionContainer>
    );
}

export default AccountListPage; 